import { Link, createFileRoute, useLoaderData, useNavigate } from "@tanstack/react-router";
import { useCity } from "@/context/CityContext";
import { usePublicPlaces } from "@/hooks/use-public-places";
import { usePublicCities, type PublicCity } from "@/hooks/use-public-cities";

import MiniPlatformMap from "@/components/MiniPlatformMap";
import { RelatedHubs } from "@/components/RelatedHubs";
import { ArrowUpRight, MapPin, Users, Calendar, Sparkles, Image as ImageIcon } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { buildHreflangLinks, buildLocaleMeta } from "@/lib/i18n-paths";
import { getPublicCities } from "@/lib/cities-public.functions";

import { CitiesPage } from "@/components/city/CitiesPage";
export const Route = createFileRoute("/cities")({
  loader: async () => {
    try {
      const cities = await getPublicCities();
      return { cities };
    } catch {
      return { cities: [] as Awaited<ReturnType<typeof getPublicCities>> };
    }
  },
  head: ({ loaderData }) => {
    const cities = loaderData?.cities ?? [];
    const url = "https://whatfinland.com/cities";
    const breadcrumbs = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "what Finland", item: "https://whatfinland.com/" },
        { "@type": "ListItem", position: 2, name: "Cities", item: url },
      ],
    };
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": url,
      name: "Finnish Cities Covered by what Finland",
      url,
      numberOfItems: cities.length,
      itemListOrder: "https://schema.org/ItemListOrderAscending",
      itemListElement: cities.map((c, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://whatfinland.com/${c.slug}`,
        name: c.name,
        item: {
          "@type": "City",
          name: c.name,
          url: `https://whatfinland.com/${c.slug}`,
          ...(c.region ? { address: { "@type": "PostalAddress", addressRegion: c.region, addressCountry: "FI" } } : {}),
          ...(c.lat != null && c.lng != null ? { geo: { "@type": "GeoCoordinates", latitude: c.lat, longitude: c.lng } } : {}),
        },
      })),
    };
    return {
      meta: [
        { title: "Cities — what Finland" },
        {
          name: "description",
          content:
            "Switch cities on what Finland — Helsinki, Turku, Tampere, Hanko and Porvoo. Live map, curated counts and quick jumps to eat, do and happening.",
        },
        { property: "og:title", content: "Cities — what Finland" },
        {
          property: "og:description",
          content: "Switch between Helsinki, Turku, Tampere, Hanko and Porvoo on what Finland.",
        },
        { property: "og:url", content: url },
        ...buildLocaleMeta("en"),
      ],
      links: [{ rel: "canonical", href: url }, ...buildHreflangLinks("/cities")],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumbs) },
        { type: "application/ld+json", children: JSON.stringify(itemList) },
      ],
    };
  },
  component: () => (
    <>
      <CitiesPage />
      <RelatedHubs current="cities" />
    </>
  ),
});
